import { can } from '@kronos/domain'
import type { Role } from '@kronos/domain'
import type { Me } from '../../data'
import { TodayReceiving } from '../receive/TodayReceiving'
import { TodaySchedule } from '../schedule/TodaySchedule'
import { TodayTeam } from '../schedule/TodayTeam'
import { PageHeader } from '../primitives'
import { ClockCard } from './ClockCard'
import { NextJob } from './NextJob'
import { YourWork } from './YourWork'

/**
 * Today — where everyone lands, and **the one screen that is most different
 * from job to job.**
 *
 *  - A technician gets the clock, any requests to come in, and their work.
 *  - Intake gets what is arriving and a way to take it in.
 *  - An engineer gets who is in, who has been asked, and the builds on the floor.
 *
 * Someone with two jobs gets both, each under its own heading — they are
 * separate checkpoints that one person happens to hold. Each block is owned by
 * the area it comes from (`schedule/`, `receive/`); this file only decides who
 * sees which.
 */
export function TodayView({ me, roles }: { me: Me; roles: readonly Role[] }): JSX.Element {
  const date = new Date().toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' })
  const first = me.name.split(/\s+/)[0] ?? me.name
  const jobs = [
    can(roles, 'keep_hours') ? 'technician' : null,
    can(roles, 'receive_stock') ? 'intake' : null,
    can(roles, 'schedule_people') ? 'engineer' : null,
  ].filter((j): j is Job => j !== null)
  // One job: no headings, just the day. Several: each under its own name, so a
  // technician's clock and intake's deliveries read as two jobs, not one jumble.
  const titled = jobs.length > 1

  return (
    <div className="page page--narrow">
      <PageHeader eyebrow={date} title={`${greeting()}, ${first}`} />
      {jobs.map((job) => (
        <div key={job} className={titled ? 'today-job' : 'page'}>
          {titled ? <h2 className="today-job__title">{JOB_TITLE[job]}</h2> : null}
          {job === 'technician' ? (
            <>
              <ClockCard />
              <TodaySchedule />
              <NextJob meId={me.id} />
            </>
          ) : null}
          {job === 'intake' ? <TodayReceiving /> : null}
          {job === 'engineer' ? (
            <>
              <TodayTeam />
              <YourWork meId={me.id} roles={roles} />
            </>
          ) : null}
        </div>
      ))}
      {jobs.length === 0 ? <p className="muted">Scan a label to open the part it is on.</p> : null}
    </div>
  )
}

type Job = 'technician' | 'intake' | 'engineer'

const JOB_TITLE: Readonly<Record<Job, string>> = {
  technician: 'Your shift',
  intake: 'Deliveries',
  engineer: 'The team',
}

function greeting(): string {
  const h = new Date().getHours()
  return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening'
}
